import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useRoleContext } from '../../../role/role.context'
import { useAuthContext } from '../../../context/auth.context'
import { noteView } from '../audit-api'
import { useAuditCapabilities } from '../use-audit-capabilities'
import { catalogueTableOf, useAuditCatalogue } from '../use-audit-catalogue'
import { useCurrentAuditRecord } from './audit-record-context'
import { eventHistoryServed } from './record-routes'
import { HistoryDrawer } from './history-drawer'

const NO_ROLES = { orgRoles: {}, sysRoles: {} }
export const SETTLE_MS = 250

// A record editor reads its History without the audit log (phase 4 ruling
// 6): the update right on the record's permission (role.org), as RoleOrg
// decides it. The server decides again on every read.
export const canEditRecord = (role, roles, auth) => {
  if (!role) return false
  const r = roles || NO_ROLES
  if (role.org) return Boolean(auth.isSysRoot || auth.isSysAdmin || (r.orgRoles && r.orgRoles[role.org] && r.orgRoles[role.org].accessUpdate))
  return false
}

// A platform record (registry org_scope platform or cross_org: an app, a
// system role, an error, a login, an LMS preset) is read through the system
// audit log only. Content answers 403 to anyone without SYS_AUDIT_LOG read
// (root passes every system scope), and neither the organization's audit log
// nor a role on the record changes that. The button shows exactly when the
// read would be served.
export const canReadPlatformRecord = (caps, auth) => Boolean(auth.isSysRoot || (caps && caps.sysAuditLog))

// A staff page's row-scoped record (record.recordOrg, record-routes.js) can
// belong to any organization. Content serves its History to a reader
// without SYS_AUDIT_LOG only when it is the reader's current organization's
// (gate.go: any other answers 404, and so does a row of no organization):
// the reader's organization audit log, or an editor's right, is their
// current organization's alone. Unknown (not yet loaded, or nothing says
// it): not theirs.
export const inCurrentOrganization = (record, auth) =>
  record.organizationID !== undefined && record.organizationID !== null && auth.organizationID !== undefined && auth.organizationID !== null &&
  Number(record.organizationID) === Number(auth.organizationID)

// Whether POST /views can record a view of this type: one the catalogue
// lists (an audited type this reader may see) and not a platform
// definition (apps, roles), which content answers with 400 because it
// belongs to no organization. Cross-organization types the catalogue can't
// tell apart (logins) answer 400 too; noteView treats that as "not
// recorded".
export const recordsViews = (catalogue, type) => {
  const t = catalogueTableOf(catalogue, type)
  return Boolean(t && !t.platform)
}

const isImpersonating = auth => Boolean(auth.jwtClaims && auth.jwtClaims.impersonationEndsAt)

// The page template's History (spec §5): shown on any page whose record the
// chrome resolves (ruling 1), and records the view once per record visit
// (ruling 4): moving between a record's sub-pages, filtering or opening the
// drawer records nothing more; leaving the record and coming back is a new
// visit. Nothing is recorded while impersonating, nor for a section's top
// (not a record). ?history=1 opens the drawer once (a node's ⋮ "History"),
// then leaves the address.
export const HistoryButton = () => {
  const record = useCurrentAuditRecord()
  const caps = useAuditCapabilities()
  const catalogue = useAuditCatalogue()
  const roles = useRoleContext()
  const { state: auth } = useAuthContext()
  const [open, setOpen] = useState(false)
  const posted = useRef(null)
  const location = useLocation()
  const routerHistory = useHistory()

  const key = record ? `${record.type}:${record.id}` : null
  const impersonating = isImpersonating(auth) || Boolean(caps.impersonating)
  const viewable = Boolean(record) && !record.root && recordsViews(catalogue, record.type)
  // A learning-activity reader (activityLog) can't read record= (it needs the
  // audit log): only an editor of the record gets History then. A platform
  // record is the system audit log's alone; a staff page's record of any
  // organization is the system audit log's, or the reader's in their own
  // organization.
  const sysRead = canReadPlatformRecord(caps, auth)
  const orgRead = Boolean(record) && ((caps.canAudit && !caps.activityLog) || canEditRecord(record.role, roles, auth))
  // A type recorded through named events only is served from the content
  // that knows them (record-routes.js eventHistoryServed).
  const allowed = Boolean(record) && eventHistoryServed(catalogue, record.type) && (record.platform
    ? sysRead
    : record.recordOrg
      ? sysRead || (inCurrentOrganization(record, auth) && orgRead)
      : orgRead)
  // the page hasn't said yet whose record it is (useAuditRecord's
  // organizationID): ?history=1 waits for it
  const orgPending = Boolean(record) && record.recordOrg === 'page' && record.organizationID === undefined && !sysRead
  const asked = new URLSearchParams(location.search).get('history') === '1'

  useEffect(() => {
    if (!key) {
      posted.current = null
      return undefined
    }
    if (!auth.isAuth || impersonating || !viewable || posted.current === key) return undefined
    // The page settles first: a page that declares its record does so in
    // its own effect, after this one, and a redirect passes straight
    // through. Only the record still shown SETTLE_MS later is recorded.
    const t = setTimeout(() => {
      posted.current = key
      const [type, ...rest] = key.split(':')
      // a refused view is never shown: 400/404/413/429 are expected (an
      // unrecordable type, another organization's record, the budget), and
      // a failure to record a view is not the reader's problem
      noteView(type, rest.join(':')).catch(() => {})
    }, SETTLE_MS)
    return () => clearTimeout(t)
  }, [key, auth.isAuth, impersonating, viewable])

  useEffect(() => {
    setOpen(false)
  }, [key])

  // ?history=1: once the reader's capabilities are known, open the drawer
  // if they may, and take the parameter out of the address either way.
  useEffect(() => {
    if (!asked || !caps.loaded || orgPending) return
    if (allowed) setOpen(true)
    const s = new URLSearchParams(location.search)
    s.delete('history')
    const rest = s.toString()
    routerHistory.replace({ pathname: location.pathname, search: rest ? `?${rest}` : '', state: location.state })
  }, [asked, caps.loaded, allowed, orgPending, key]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!record || !allowed) return null

  return (
    <span className="sw-workspace d-inline-flex">
      <button type="button" className="btn sw-btn btn-sm my-1" aria-haspopup="dialog" onClick={() => setOpen(true)}>History</button>
      <HistoryDrawer key={key} record={record} open={open} onClose={() => setOpen(false)} />
    </span>
  )
}
